/**
 * GoTaps — API Service Layer
 *
 * All data fetching lives here. Connected to the GoTap backend.
 * Enterprise API uses Sanctum Bearer token auth.
 */

// ── Config ───────────────────────────────────────────────────────
const RAW_API = (process.env.REACT_APP_API_BASE || 'http://teams.gotap.oo/api').replace(/\/+$/, '');
const API_BASE = /\/api$/.test(RAW_API) ? RAW_API : `${RAW_API}/api`;

// 401 auto-logout callback — set by AppContext
let _onAuthError = null;
export function setAuthErrorHandler(fn) { _onAuthError = fn; }

function getToken() {
  return localStorage.getItem('gotap_token');
}

function setToken(token) {
  localStorage.setItem('gotap_token', token);
}

function clearToken() {
  localStorage.removeItem('gotap_token');
}

async function request(endpoint, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await res.json();

  if (!res.ok) {
    // Auto-logout on 401 (expired/invalid token)
    if (res.status === 401 && _onAuthError) {
      _onAuthError();
    }
    const err = new Error(data.message || 'Request failed');
    err.status = res.status;
    err.errors = data.errors || null;
    err.data = data; // preserve full response body
    throw err;
  }

  return data;
}

// ── Auth ──────────────────────────────────────────────────────────
export async function login(email, password) {
  try {
    const data = await request('/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (data.token) setToken(data.token);
    return data;
  } catch (err) {
    throw err;
  }
}

export async function logout() {
  try {
    await request('/logout');
  } finally {
    clearToken();
  }
}

export async function forgotPassword(email) {
  return request('/forgetPassword', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function verifyOtp(email, otp) {
  return request('/otpVerification', {
    method: 'POST',
    body: JSON.stringify({ email, otp }),
  });
}

export async function resetPassword(email, otp, password, password_confirmation) {
  const data = await request('/resetPassword', {
    method: 'POST',
    body: JSON.stringify({ email, otp, password, password_confirmation }),
  });
  if (data.token) setToken(data.token);
  return data;
}

// ── Change Password ──────────────────────────────────────────────
export async function changePassword(old_password, new_password) {
  return request('/changePassword', {
    method: 'POST',
    body: JSON.stringify({ old_password, new_password }),
  });
}

// ── Delete Enterprise Account ───────────────────────────────────
export async function deleteEnterpriseAccount(password) {
  return request('/enterprise/delete-account', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

// ── Reactivate Deactivated Account ───────────────────────────────
export async function reactivateAccount(email, password) {
  const data = await request('/enterprise/reactivate', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  if (data.token) setToken(data.token);
  return data;
}

// ── Enterprise Registration ──────────────────────────────────────
export async function registerEnterprise(formData) {
  const res = await fetch(`${API_BASE}/registerEnterprise`, {
    method: 'POST',
    headers: { 'Accept': 'application/json' },
    body: formData, // FormData for file upload (contract)
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.message || 'Registration failed');
    err.status = res.status;
    err.errors = data.errors || null;
    throw err;
  }
  return data;
}

// ── Validate set-password token ──────────────────────────────────
export async function validateSetPasswordToken(token) {
  return request(`/enterprise/validate-token/${token}`);
}

// ── Set password (from admin email link) ─────────────────────────
export async function setupPassword(token, password, password_confirmation) {
  return request('/enterprise/set-password', {
    method: 'POST',
    body: JSON.stringify({ token, password, password_confirmation }),
  });
}

// ── Support Inquiry ──────────────────────────────────────────────
export async function submitSupportInquiry(data) {
  return request('/enterprise/support-inquiry', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ── Check if user is authenticated (has valid token) ─────────────
export function isAuthenticated() {
  return !!getToken();
}

export { getToken, setToken, clearToken };

// ── Enterprise ───────────────────────────────────────────────────
export async function getSubscription() {
  return request('/enterprise/subscription');
}

export async function updateEnterpriseAccount(data) {
  return request('/updateEnterpriseAccount', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ── Members / Profiles ───────────────────────────────────────────
export async function getMembers(page = 1, search = '') {
  const params = new URLSearchParams({ page });
  if (search) params.append('search', search);
  return request(`/enterprise/profiles?${params}`);
}

export async function getMemberById(id) {
  const data = await request(`/enterprise/profiles/${id}`);
  return data.data || data;
}

export async function createMember(formData) {
  // If FormData (file upload), use fetch directly without Content-Type
  if (formData instanceof FormData) {
    const token = getToken();
    const res = await fetch(`${API_BASE}/enterprise/profiles`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) {
      const err = new Error(data.message || 'Create failed');
      err.errors = data.errors || null;
      throw err;
    }
    return data.data || data;
  }
  // JSON fallback
  const data = await request('/enterprise/profiles', {
    method: 'POST',
    body: JSON.stringify(formData),
  });
  return data.data || data;
}

export async function updateMember(id, formData) {
  const data = await request(`/enterprise/profiles/${id}`, {
    method: 'POST',
    body: JSON.stringify(formData),
  });
  return data.data || data;
}

export async function updateMemberForm(id, formData) {
  const token = getToken();
  const res = await fetch(`${API_BASE}/enterprise/profiles/${id}`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.message || 'Update failed');
    err.errors = data.errors || null;
    throw err;
  }
  return data;
}

export async function deleteMember(id) {
  return request(`/enterprise/profiles/${id}`, { method: 'DELETE' });
}

export async function getMemberManage(id) {
  return request(`/enterprise/profiles/${id}/manage`);
}

export async function disconnectMember(id) {
  return request(`/enterprise/profiles/${id}/disconnect`, { method: 'POST' });
}

export async function toggleDirect(id) {
  return request(`/enterprise/profiles/${id}/toggle-direct`, { method: 'POST' });
}

export async function togglePrivate(id) {
  return request(`/enterprise/profiles/${id}/toggle-private`, { method: 'POST' });
}

// ── Platform Links ───────────────────────────────────────────────
export async function getAvailablePlatforms() {
  return request('/enterprise/platforms');
}

export async function addProfileLink(profileId, data) {
  return request(`/enterprise/profiles/${profileId}/links`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateProfileLink(profileId, linkId, data) {
  return request(`/enterprise/profiles/${profileId}/links/${linkId}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteProfileLink(profileId, linkId) {
  return request(`/enterprise/profiles/${profileId}/links/${linkId}`, { method: 'DELETE' });
}

export async function bulkSaveLinks(profileId, links) {
  return request(`/enterprise/profiles/${profileId}/links-bulk`, {
    method: 'POST',
    body: JSON.stringify({ links }),
  });
}

// ── Subteams ─────────────────────────────────────────────────────
export async function getSubteams() {
  return request('/enterprise/subteams');
}

export async function createSubteam(formData) {
  const data = await request('/enterprise/subteams', {
    method: 'POST',
    body: JSON.stringify(formData),
  });
  return data;
}

export async function updateSubteam(id, formData) {
  return request(`/enterprise/subteams/${id}`, {
    method: 'POST',
    body: JSON.stringify(formData),
  });
}

export async function deleteSubteam(id) {
  return request(`/enterprise/subteams/${id}`, { method: 'DELETE' });
}

export async function getSubteamProfiles(id) {
  return request(`/enterprise/subteams/${id}/profiles`);
}

export async function addProfileToSubteam(subteamId, profileId) {
  return request(`/enterprise/subteams/${subteamId}/profiles`, {
    method: 'POST',
    body: JSON.stringify({ profile_id: profileId }),
  });
}

export async function removeProfileFromSubteam(subteamId, profileId) {
  return request(`/enterprise/subteams/${subteamId}/profiles/${profileId}`, { method: 'DELETE' });
}

export async function getSubteamStats(id) {
  return request(`/enterprise/subteams/${id}/stats`);
}

export async function getAvailableSubteamProfiles(currentSubteamId = null) {
  const params = new URLSearchParams();
  if (currentSubteamId) params.append('current_subteam_id', currentSubteamId);
  return request(`/enterprise/subteams/available-profiles?${params}`);
}


// ── Campaigns ────────────────────────────────────────────────────
export async function getCampaigns(page = 1, status = '') {
  const params = new URLSearchParams({ page });
  if (status && status !== 'all') params.append('status', status);
  const data = await request(`/enterprise/email-campaigns?${params}`);
  return data.data || data;
}

export async function createCampaign(formData) {
  const data = await request('/enterprise/email-campaigns', {
    method: 'POST',
    body: JSON.stringify(formData),
  });
  return data.data || data;
}

export async function updateCampaign(id, formData) {
  const data = await request(`/enterprise/email-campaigns/${id}`, {
    method: 'POST',
    body: JSON.stringify(formData),
  });
  return data.data || data;
}

export async function cancelCampaign(id) {
  const data = await request(`/enterprise/email-campaigns/${id}/cancel`, {
    method: 'POST',
  });
  return data.data || data;
}

export async function getCampaignRecipients() {
  return request('/enterprise/email-campaigns/recipients');
}

export async function getCampaignDetail(id) {
  const data = await request(`/enterprise/email-campaigns/${id}`);
  return data.data || data;
}

export async function deleteCampaign(id) {
  return request(`/enterprise/email-campaigns/${id}`, { method: 'DELETE' });
}

export async function generateAiEmail({ prompt, subject = '', recipientMode = '' }) {
  const data = await request('/enterprise/email-campaigns/generate-content', {
    method: 'POST',
    body: JSON.stringify({
      prompt,
      subject,
      recipient_mode: recipientMode,
    }),
  });
  return data.data || data;
}

// ── Leads ────────────────────────────────────────────────────────
export async function getLeads(search = '', status = '') {
  const params = new URLSearchParams({ per_page: 50 });
  if (search) params.append('search', search);
  if (status && status !== 'all') params.append('status', status);
  const data = await request(`/EnterpriseLeads?${params}`);
  return data.data || data;
}

export async function addLead(leadData) {
  const data = await request('/addLead', {
    method: 'POST',
    body: JSON.stringify(leadData),
  });
  return data;
}

export async function deleteLead(id) {
  return request('/deleteLead', {
    method: 'POST',
    body: JSON.stringify({ id }),
  });
}

export async function updateLeadNote(id, note) {
  return request('/UpdateLeadNote', {
    method: 'POST',
    body: JSON.stringify({ id, note }),
  });
}

export async function sendLeadEmail(recipients, subject, body) {
  return request('/SendLeadEmail', {
    method: 'POST',
    body: JSON.stringify({ recipients, subject, body }),
  });
}

export async function getLeadActivity(leadId) {
  const data = await request(`/LeadActivity?lead_id=${encodeURIComponent(leadId)}`);
  return data.data || data;
}

export async function updateLead(id, data) {
  return request('/UpdateProfileLead', {
    method: 'POST',
    body: JSON.stringify({ id, ...data }),
  });
}

export async function getEnterpriseProfiles() {
  const data = await request('/enterprise/profiles?per_page=100');
  const items = data?.data?.data || data?.data || [];
  return Array.isArray(items) ? items : [];
}

export async function getLeadsMap() {
  const data = await request('/ProfileLeadsMap');
  return data.data || data;
}

// ── Requests ─────────────────────────────────────────────────────
export async function getRequests(page = 1, { search = '', status = '', sort_by = '' } = {}) {
  const params = new URLSearchParams({ page });
  if (search) params.append('search', search);
  if (status) params.append('status', status);
  if (sort_by) params.append('sort_by', sort_by);
  return request(`/enterprise/requests?${params}`);
}

export async function acceptRequest(id) {
  return request(`/enterprise/requests/${id}/link`, { method: 'POST' });
}

export async function deleteRequest(id) {
  return request(`/enterprise/requests/${id}`, { method: 'DELETE' });
}

// ── Insights / Analytics ─────────────────────────────────────────
export async function getInsights() {
  const data = await request('/enterprise/insights');
  return data.data || data;
}

// ── Dashboard Widget Endpoints ───────────────────────────────
export async function getDashboardStats() {
  return request('/enterprise/dashboard/stats');
}

export async function getDashboardTopPerformers() {
  return request('/enterprise/dashboard/top-performers');
}

export async function getDashboardLeadSources() {
  return request('/enterprise/dashboard/lead-sources');
}

export async function getDashboardWeeklyPerformance() {
  return request('/enterprise/dashboard/weekly-performance');
}

export async function getDashboardLeadQueue() {
  return request('/enterprise/dashboard/lead-queue');
}

export async function getDashboardTopRegions() {
  return request('/enterprise/dashboard/top-regions');
}

export async function getDashboardTapPerformance() {
  return request('/enterprise/dashboard/tap-performance');
}

export async function getDashboardTrafficSources() {
  return request('/enterprise/dashboard/traffic-sources');
}

export async function getDashboardFunnel() {
  return request('/enterprise/dashboard/funnel');
}

export async function getDashboardCampaignPerformance() {
  return request('/enterprise/dashboard/campaign-performance');
}

export async function getDashboardMonthlyGoals() {
  return request('/enterprise/dashboard/monthly-goals');
}

export async function getDashboardNfcStatus() {
  return request('/enterprise/dashboard/nfc-status');
}

export async function getDashboardRecentLeads() {
  return request('/enterprise/dashboard/recent-leads');
}

export async function getDashboardTrendOverview(period = 'month') {
  return request(`/enterprise/dashboard/trend-overview?period=${period}`);
}

export async function getDashboardActivityHeatmap(period = 'month') {
  return request(`/enterprise/dashboard/activity-heatmap?period=${period}`);
}

export async function getDashboardAiStats() {
  return request('/enterprise/dashboard/ai-stats');
}

export async function getDashboardLeaderboard(period = 'month') {
  return request(`/enterprise/dashboard/leaderboard?period=${period}`);
}

// ── Profile Cards (NFC card assignment via QR scan) ──────────────
export async function getProfileCards(profileId) {
  const data = await request(`/enterprise/profiles/${profileId}/cards`);
  return data.data || [];
}

export async function assignCard(profileId, { uuid, expected_type }) {
  const data = await request(`/enterprise/profiles/${profileId}/cards`, {
    method: 'POST',
    body: JSON.stringify({ uuid, expected_type }),
  });
  return data.data || data;
}

export async function toggleCard(profileId, cardId) {
  const data = await request(`/enterprise/profiles/${profileId}/cards/${cardId}/toggle`, {
    method: 'POST',
  });
  return data.data || data;
}

export async function unassignCard(profileId, cardId) {
  return request(`/enterprise/profiles/${profileId}/cards/${cardId}`, {
    method: 'DELETE',
  });
}

// ── Virtual Background ───────────────────────────────────────────
export async function getVirtualBackground(profileId) {
  return request(`/enterprise/profiles/${profileId}/virtual-background`);
}

export async function updateVirtualBackground(profileId, data) {
  return request(`/enterprise/profiles/${profileId}/virtual-background`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ── Email Signature ──────────────────────────────────────────────
export async function getEmailSignature(profileId) {
  return request(`/enterprise/profiles/${profileId}/email-signature`);
}

export async function updateEmailSignature(profileId, data) {
  return request(`/enterprise/profiles/${profileId}/email-signature`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

